import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  Zap,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO50001_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Zap size={18} className="text-green-400" />
            <span>Energy Management Certification</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400">
            ISO 50001 Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Energy Management System - Improving Energy Performance, Reducing
            Costs, and Supporting Sustainability Goals
          </p>
        </motion.div>
      </section>

      {/* What is ISO 50001 */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is ISO 50001 Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              ISO 50001 is an internationally recognized standard for Energy
              Management Systems (EnMS). It helps organizations establish a
              structured approach to improving energy performance, reducing
              energy costs, and lowering greenhouse gas emissions. The standard
              is applicable to organizations of all sizes and sectors,
              regardless of geographical location.
            </p>
            <p>
              ISO 50001 certification confirms that an organization has
              implemented an effective Energy Management System that enables
              continual improvement in energy performance, including energy
              efficiency, energy use, and energy consumption.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of ISO 50001 Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Energy Performance Improvement",
                desc: "Helps organizations reduce energy consumption and optimize energy use through data-driven decisions.",
                icon: TrendingUp,
              },
              {
                title: "Cost Reduction",
                desc: "Lowers operational costs by minimizing energy waste and improving efficiency.",
                icon: BarChart3,
              },
              {
                title: "Environmental Sustainability",
                desc: "Supports reduction of greenhouse gas emissions and alignment with climate goals.",
                icon: Zap,
              },
              {
                title: "Regulatory Compliance",
                desc: "Assists organizations in meeting national energy regulations and sustainability frameworks.",
                icon: CheckCircle,
              },
              {
                title: "Integration with Other Standards",
                desc: "Easily integrates with ISO 9001, ISO 14001, and ISO 45001.",
                icon: Award,
              },
              {
                title: "Data-Driven Management",
                desc: "Establishes reliable energy data baselines for informed decision-making.",
                icon: Target,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Energy Management System */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is an Energy Management System (EnMS)?
          </h2>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              An Energy Management System (EnMS) is a structured framework that
              enables organizations to:
            </p>
            <ul className="space-y-3">
              {[
                "Monitor and control energy consumption",
                "Identify energy-saving opportunities",
                "Establish energy objectives and action plans",
                "Measure energy performance improvements",
                "Ensure continual improvement through audits and reviews",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-gray-700 mt-6">
              An effective EnMS embeds energy efficiency into daily operations
              and long-term strategy.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of ISO 50001 Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Improves energy efficiency and performance",
              "Reduces energy costs and operational expenses",
              "Enhances sustainability and carbon reduction initiatives",
              "Strengthens compliance with energy regulations",
              "Improves organizational awareness and accountability for energy use",
              "Supports ESG, net-zero, and sustainability reporting",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of ISO 50001 Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            ISO 50001 helps organizations:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Establish control over energy use and consumption",
              "Improve decision-making using reliable energy data",
              "Reduce environmental impact and carbon footprint",
              "Enhance credibility with regulators, customers, and investors",
              "Support long-term sustainability and energy security",
              "Achieve competitive advantage in sustainability",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-green-100 to-emerald-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-green-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of ISO 50001 Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Measurable reduction in energy consumption",
              "Lower utility and operational costs",
              "Improved environmental performance",
              "Enhanced corporate sustainability reputation",
              "Better compliance with legal and regulatory requirements",
              "Improved internal energy awareness and engagement",
              "Continuous improvement in energy performance",
              "Enhanced stakeholder confidence and trust",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Commonly Require ISO 50001
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            ISO 50001 is widely adopted in energy-intensive and
            sustainability-focused sectors:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Manufacturing",
              "Oil & Gas",
              "Cement, Steel, and Heavy Industry",
              "Food & Beverage Processing",
              "Pharmaceuticals",
              "Data Centers and IT Infrastructure",
              "Construction & Real Estate",
              "Utilities and Public Sector Organizations",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get ISO 50001 Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Energy Review and Gap Analysis",
                desc: "Conduct a thorough energy review and assess gaps against ISO 50001 requirements",
              },
              {
                step: 2,
                title: "Establish Baselines and EnPIs",
                desc: "Establish energy baselines and define Energy Performance Indicators (EnPIs)",
              },
              {
                step: 3,
                title: "Identify SEUs",
                desc: "Identify Significant Energy Uses (SEUs) in your organization",
              },
              {
                step: 4,
                title: "Document EnMS",
                desc: "Develop EnMS documentation and implement control measures",
              },
              {
                step: 5,
                title: "Energy Action Plans",
                desc: "Implement energy action plans to improve performance",
              },
              {
                step: 6,
                title: "Staff Training",
                desc: "Provide comprehensive training on energy management principles",
              },
              {
                step: 7,
                title: "Internal Audits",
                desc: "Conduct internal audits and management reviews",
              },
              {
                step: 8,
                title: "Certification Audit",
                desc: "Undergo certification audit by an accredited certification body",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for ISO 50001 Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides comprehensive ISO 50001 (EnMS)
              consultancy and certification support, helping organizations
              achieve measurable energy savings and sustainable performance
              improvements.
            </p>
            <p className="text-lg text-gray-700 mb-6">Our services include:</p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Energy review and data analysis",
                "EnMS documentation and implementation",
                "EnPI development and monitoring",
                "Internal and pre-certification audits",
                "Certification body coordination and audit support",
                "ESG and sustainability alignment",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help organizations align ISO 50001 implementation with energy
              efficiency goals, ESG strategies, and sustainability frameworks.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default ISO50001_Details;
